import { z } from 'zod';
import { baseComponentSchema, buttonSchema } from '../component-base.schemas';

// Text-Blocks custom colors schema
export const textBlocksCustomColorsSchema = z
  .object({
    background: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    title: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    text: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    border: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    buttonBg: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    buttonText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
  })
  .optional();

// Text-Blocks component schemas
export const textBlockSchema = z.object({
  id: z.string().uuid('Invalid text block ID'),
  title: z.string().max(200, 'Block title is too long').optional(),
  content: z.string().min(1, 'Block content is required').max(10000, 'Block content is too long'),
  order: z.number().int().min(0, 'Order must be non-negative'),

  // Block-specific styling
  titleTag: z.enum(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span']).default('h3').optional(),
  contentTag: z
    .enum(['p', 'div', 'span', 'blockquote', 'article', 'section'])
    .default('p')
    .optional(),

  // Typography styling
  titleSize: z
    .enum(['xs', 'sm', 'base', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl'])
    .default('lg')
    .optional(),
  titleWeight: z
    .enum(['thin', 'light', 'normal', 'medium', 'semibold', 'bold', 'extrabold'])
    .default('semibold')
    .optional(),
  titleColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid title color format')
    .optional(),

  contentSize: z.enum(['xs', 'sm', 'base', 'lg', 'xl', '2xl']).default('base').optional(),
  contentWeight: z
    .enum(['thin', 'light', 'normal', 'medium', 'semibold', 'bold'])
    .default('normal')
    .optional(),
  contentColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid content color format')
    .optional(),

  // Alignment and spacing
  titleAlignment: z.enum(['left', 'center', 'right', 'justify']).default('left').optional(),
  contentAlignment: z.enum(['left', 'center', 'right', 'justify']).default('left').optional(),

  // Block spacing (pixels)
  marginTop: z.number().int().min(0).max(200).default(0).optional(),
  marginBottom: z.number().int().min(0).max(200).default(20).optional(),
  paddingTop: z.number().int().min(0).max(100).default(0).optional(),
  paddingBottom: z.number().int().min(0).max(100).default(0).optional(),
  paddingLeft: z.number().int().min(0).max(100).default(0).optional(),
  paddingRight: z.number().int().min(0).max(100).default(0).optional(),

  // Background and borders
  backgroundColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid background color format')
    .optional(),
  borderColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid border color format')
    .optional(),
  borderWidth: z.number().int().min(0).max(20).default(0).optional(),
  borderStyle: z.enum(['none', 'solid', 'dashed', 'dotted', 'double']).default('none').optional(),
  borderRadius: z.number().int().min(0).max(50).default(0).optional(),

  // Shadow and effects
  boxShadow: z.string().max(100, 'Box shadow value too long').optional(),
  opacity: z.number().min(0).max(1).default(1).optional(),

  // Advanced styling
  gradient: z
    .object({
      enabled: z.boolean().default(false),
      direction: z
        .enum(['to-right', 'to-left', 'to-bottom', 'to-top', 'to-bottom-right', 'to-bottom-left'])
        .default('to-right')
        .optional(),
      startColor: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid start color format')
        .optional(),
      endColor: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid end color format')
        .optional(),
    })
    .optional(),

  // Responsive behavior
  hideOnMobile: z.boolean().default(false).optional(),
  hideOnTablet: z.boolean().default(false).optional(),
  hideOnDesktop: z.boolean().default(false).optional(),

  // Interactive features
  isCollapsible: z.boolean().default(false).optional(),
  defaultCollapsed: z.boolean().default(false).optional(),

  // Links and CTAs
  link: z
    .object({
      url: z.string().url('Invalid link URL'),
      target: z.enum(['_blank', '_self']).default('_self'),
      title: z.string().max(100, 'Link title too long').optional(),
    })
    .optional(),

  // Custom CSS classes and styling
  customClasses: z
    .array(z.string().max(50, 'CSS class name too long'))
    .max(20, 'Too many custom classes')
    .optional(),
  customStyles: z.record(z.string().max(200, 'CSS property value too long')).optional(),
});

export const textBlocksDataSchema = z.object({
  // Component header
  componentTitle: z.string().max(200, 'Component title is too long').optional(),
  componentSubtitle: z.string().max(300, 'Component subtitle is too long').optional(),
  componentDescription: z.string().max(1000, 'Component description is too long').optional(),

  // Text blocks array
  blocks: z
    .array(textBlockSchema)
    .min(1, 'At least one text block is required')
    .max(50, 'Too many text blocks'),

  // Global layout options
  // Note: 'horizontal' and 'columns' are deprecated but kept for backwards compatibility
  layout: z.enum(['vertical', 'horizontal', 'grid', 'masonry', 'columns']).default('vertical'),
  columns: z.enum(['1', '2', '3', '4', '5', '6']).default('1').optional(),
  gap: z.enum(['none', 'xs', 'sm', 'md', 'lg', 'xl', '2xl']).default('md').optional(),
  horizontalPadding: z.enum(['none', 'xs', 'sm', 'md', 'lg', 'xl', '2xl']).default('md').optional(),

  // Global alignment
  headerAlignment: z.enum(['left', 'center', 'right', 'justify']).default('center').optional(),
  globalAlignment: z.enum(['left', 'center', 'right', 'justify']).default('left').optional(),
  verticalAlignment: z.enum(['top', 'center', 'bottom', 'stretch']).default('top').optional(),

  // Container styling
  containerPadding: z
    .object({
      top: z.number().int().min(0).max(200).default(40),
      bottom: z.number().int().min(0).max(200).default(40),
      left: z.number().int().min(0).max(200).default(40),
      right: z.number().int().min(0).max(200).default(40),
    })
    .optional(),
  containerMargin: z
    .object({
      top: z.number().int().min(0).max(200).default(0),
      bottom: z.number().int().min(0).max(200).default(0),
      left: z.number().int().min(0).max(200).default(0),
      right: z.number().int().min(0).max(200).default(0),
    })
    .optional(),

  // Global background and styling
  backgroundColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid background color format')
    .optional(),
  backgroundImage: z.string().url('Invalid background image URL').optional(),
  backgroundSize: z.enum(['cover', 'contain', 'auto']).default('cover').optional(),
  backgroundPosition: z
    .enum(['center', 'top', 'bottom', 'left', 'right'])
    .default('center')
    .optional(),
  backgroundRepeat: z
    .enum(['no-repeat', 'repeat', 'repeat-x', 'repeat-y'])
    .default('no-repeat')
    .optional(),

  // Global borders and effects
  borderColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid border color format')
    .optional(),
  borderWidth: z.number().int().min(0).max(20).default(0).optional(),
  borderStyle: z.enum(['none', 'solid', 'dashed', 'dotted', 'double']).default('none').optional(),
  borderRadius: z.number().int().min(0).max(50).default(0).optional(),
  boxShadow: z.string().max(100, 'Box shadow value too long').optional(),

  // Global text defaults
  defaultTitleTag: z
    .enum(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span'])
    .default('h3')
    .optional(),
  defaultContentTag: z
    .enum(['p', 'div', 'span', 'blockquote', 'article', 'section'])
    .default('p')
    .optional(),
  defaultTitleSize: z
    .enum(['xs', 'sm', 'base', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl'])
    .default('lg')
    .optional(),
  defaultTitleWeight: z
    .enum(['thin', 'light', 'normal', 'medium', 'semibold', 'bold', 'extrabold'])
    .default('semibold')
    .optional(),
  defaultTitleColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid default title color format')
    .optional(),
  defaultContentSize: z.enum(['xs', 'sm', 'base', 'lg', 'xl', '2xl']).default('base').optional(),
  defaultContentWeight: z
    .enum(['thin', 'light', 'normal', 'medium', 'semibold', 'bold'])
    .default('normal')
    .optional(),
  defaultContentColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid default content color format')
    .optional(),

  // Responsive behavior (horizontal/columns deprecated but kept for backwards compatibility)
  mobileLayout: z
    .enum(['vertical', 'horizontal', 'grid', 'masonry', 'columns'])
    .default('vertical')
    .optional(),
  mobileColumns: z.enum(['1', '2', '3']).default('1').optional(),
  tabletLayout: z
    .enum(['vertical', 'horizontal', 'grid', 'masonry', 'columns'])
    .default('vertical')
    .optional(),
  tabletColumns: z.enum(['1', '2', '3', '4']).default('2').optional(),

  // Animation and interaction
  animateOnScroll: z.boolean().default(false).optional(),
  animationType: z
    .enum(['fade', 'slide-up', 'slide-down', 'slide-left', 'slide-right', 'zoom', 'stagger'])
    .default('fade')
    .optional(),
  animationDelay: z.number().int().min(0).max(2000).default(0).optional(),
  animationDuration: z.number().int().min(100).max(3000).default(600).optional(),
  staggerDelay: z.number().int().min(0).max(500).default(100).optional(),

  // Advanced features
  enableSearch: z.boolean().default(false).optional(),
  enableSort: z.boolean().default(false).optional(),
  enableFilter: z.boolean().default(false).optional(),

  // Block management
  allowAddBlocks: z.boolean().default(false).optional(),
  allowEditBlocks: z.boolean().default(false).optional(),
  allowDeleteBlocks: z.boolean().default(false).optional(),
  allowReorderBlocks: z.boolean().default(false).optional(),

  // SEO and meta
  schema: z
    .object({
      enabled: z.boolean().default(false),
      type: z
        .enum(['Article', 'BlogPosting', 'NewsArticle', 'Report', 'ScholarlyArticle'])
        .default('Article')
        .optional(),
      headline: z.string().max(200, 'Schema headline too long').optional(),
      author: z.string().max(100, 'Schema author too long').optional(),
      datePublished: z.string().optional(),
      dateModified: z.string().optional(),
    })
    .optional(),

  // Print styling
  printOptimized: z.boolean().default(false).optional(),
  printPageBreaks: z.enum(['auto', 'always', 'avoid']).default('auto').optional(),

  // Accessibility
  a11y: z
    .object({
      skipLinksEnabled: z.boolean().default(false).optional(),
      headingStructure: z.boolean().default(true).optional(),
      landmarkRoles: z.boolean().default(true).optional(),
      focusManagement: z.boolean().default(true).optional(),
    })
    .optional(),

  // Custom CSS and JavaScript
  customCSS: z.string().max(5000, 'Custom CSS too long').optional(),
  customJS: z.string().max(2000, 'Custom JS too long').optional(),

  // NEW: Component-specific custom colors that override theme colors
  customColors: textBlocksCustomColorsSchema,

  // Optional call-to-action buttons
  buttons: z.array(buttonSchema).optional(),
});

export const textBlocksComponentSchema = baseComponentSchema.extend({
  type: z.literal('text-blocks'),
  data: textBlocksDataSchema,
});

// Type exports
export type TextBlock = z.infer<typeof textBlockSchema>;
export type TextBlocksData = z.infer<typeof textBlocksDataSchema>;
export type TextBlocksComponent = z.infer<typeof textBlocksComponentSchema>;
